import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Users, X, Recycle } from 'lucide-react';

const Footer = () => {
  const [visitorCount, setVisitorCount] = useState(0);
  const [showUpcycledModal, setShowUpcycledModal] = useState(false);

  useEffect(() => {
    const trackVisitor = async () => {
      try {
        // Check if this session already counted
        const counted = sessionStorage.getItem('visitor_counted');
        if (!counted) {
          const result = await api.incrementVisitorCount();
          setVisitorCount(result.count);
          sessionStorage.setItem('visitor_counted', 'true');
        } else {
          const result = await api.getVisitorCount();
          setVisitorCount(result.count);
        }
      } catch (error) {
        console.error('Error tracking visitor:', error);
      }
    };
    trackVisitor();
  }, []);

  return (
    <>
      <footer data-testid="footer" className="bg-void border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link
                to="/"
                className="flex items-center"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_skullz-store/artifacts/et8b7zpr_IMG_20260130_130000-Photoroom.png" 
                  alt="Only Skullz" 
                  className="h-16 md:h-20 w-auto"
                />
              </Link>
              <p className="mt-4 text-neutral-400 max-w-md">
                Handcrafted skull art pieces, each one unique and made with passion. 
                Transform your space with these mesmerizing works of art.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-mono text-sm uppercase tracking-widest text-white mb-4">
                Navigate
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/shop"
                    data-testid="footer-shop-link"
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    Shop All
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    data-testid="footer-about-link"
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    About the Artist
                  </Link>
                </li>
                <li>
                  <Link
                    to="/reviews"
                    data-testid="footer-reviews-link"
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    Customer Reviews
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    data-testid="footer-contact-link"
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-mono text-sm uppercase tracking-widest text-white mb-4">
                Get In Touch
              </h4>
              <ul className="space-y-3 text-neutral-400">
                <li>Custom orders welcome</li>
                <li>Worldwide shipping</li>
                <li>
                  <a
                    href="https://customer-assets.emergentagent.com/job_skullz-store/artifacts/irm8v2iu_Donation%20Request.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    Donation Request
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => setShowUpcycledModal(true)}
                    data-testid="upcycled-notice-btn"
                    className="text-cyan-400 hover:text-cyan-300 transition-colors text-left"
                  >
                    Up-cycled Materials Notice
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-500">
              © 2025 Only Skullz LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-neutral-500">
                <Users className="w-4 h-4" />
                <span className="text-xs font-mono">
                  {visitorCount.toLocaleString()} visitors
                </span>
              </div>
              <p className="text-xs text-neutral-600 font-mono">
                Each piece is handmade & unique
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Up-cycled Materials Notice Modal */}
      {showUpcycledModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowUpcycledModal(false)}
        >
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          
          {/* Modal Content */}
          <div 
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glowing border effect */}
            <div className="absolute -inset-[1px] bg-gradient-to-r from-cyan-500 via-emerald-500 to-cyan-500 rounded-lg opacity-75 blur-sm animate-pulse" />
            
            <div className="relative bg-void-paper border border-white/10 rounded-lg overflow-hidden">
              {/* Close button */}
              <button
                onClick={() => setShowUpcycledModal(false)}
                data-testid="close-upcycled-modal"
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-neutral-400 hover:text-white bg-black/50 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header with gradient */}
              <div className="relative px-8 pt-10 pb-8 text-center overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(34,211,238,0.3)_0%,_transparent_50%)]" />
                </div>
                
                {/* Recycle icon with glow */}
                <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
                  <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-xl animate-pulse" />
                  <div className="relative w-full h-full rounded-full border-2 border-emerald-500/50 flex items-center justify-center bg-void">
                    <Recycle className="w-10 h-10 text-emerald-400" />
                  </div>
                </div>
                
                {/* Logo */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_skullz-store/artifacts/et8b7zpr_IMG_20260130_130000-Photoroom.png" 
                  alt="Only Skullz" 
                  className="h-12 mx-auto mb-4"
                />
                
                {/* Title */}
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                  Up-cycled Materials
                </h2>
                <p className="font-mono text-xs uppercase tracking-widest text-emerald-400">
                  Sustainability Notice
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

              {/* Content */}
              <div className="px-8 py-8">
                <div className="relative">
                  {/* Quote marks decoration */}
                  <span className="absolute -top-4 -left-2 text-6xl text-cyan-500/20 font-serif">"</span>
                  
                  <p className="text-neutral-300 leading-relaxed text-sm md:text-base pl-6">
                    <span className="text-white font-semibold">Disclaimer:</span> Some of the products within this website have been up-cycled from previously manufactured and lawfully acquired materials. These materials are resold and modified in accordance with all applicable laws and regulations, including the First Sale Doctrine.
                  </p>
                  
                  <p className="text-neutral-300 leading-relaxed text-sm md:text-base pl-6 mt-4">
                    All modifications, enhancements, and custom work are independently performed and are not authorized, sponsored, or endorsed by the original manufacturer.
                  </p>
                  
                  <p className="text-neutral-300 leading-relaxed text-sm md:text-base pl-6 mt-4">
                    The incorporation of up-cycled materials and custom elements does not affect the product's intended functionality or quality, and does not imply any affiliation with the original brand or rights holder.
                  </p>
                  
                  <span className="absolute -bottom-4 right-0 text-6xl text-cyan-500/20 font-serif">"</span>
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 pb-8">
                <div className="flex flex-col md:flex-row items-center justify-center gap-3 py-4 px-6 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <Recycle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  {/* Desktop: inline with bullets */}
                  <p className="hidden md:block text-xs text-emerald-300 font-mono uppercase tracking-wider">
                    Creating Art • Reducing Waste • Respecting Rights
                  </p>
                  {/* Mobile: stacked list */}
                  <ul className="md:hidden text-xs text-emerald-300 font-mono uppercase tracking-wider text-center space-y-1">
                    <li>Creating Art</li>
                    <li>Reducing Waste</li>
                    <li>Respecting Rights</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
